import { FiMail } from 'react-icons/fi';

export const task1GeneralLettersData = {
  courseId: 'task1-general-letters',
  courseType: 'writing-course',
  title: 'Task 1 General Training: Letters',
  icon: FiMail,
  overview: {
    impact: 'Master the three letter registers required by IELTS General Task 1',
    focus: 'Formal, semi-formal and informal letter conventions, structure and tone',
    uses: 'IELTS General Training Writing Task 1 only (Academic candidates write Task 1 graphs)',
    duration: '30-35 minutes',
  },
  sections: [
    {
      title: 'When You Get a Letter Task',
      content:
        '<p>Only IELTS <strong>General Training</strong> candidates write a letter for Writing Task 1. You will see a short situation (3 sentences) followed by three bullet points. Your job is to write a <strong>150-word minimum</strong> letter covering all three bullets, in the correct register.</p>',
      points: [
        'Identify the recipient first: someone you know? a stranger? a manager?',
        'Bullets are not optional. Missing one drops your Task Achievement score.',
        'Keep to one register from greeting to sign-off.',
        'No academic vocabulary tricks here — clarity, completeness and consistent tone are everything.',
      ],
    },
    {
      title: 'The Three Registers',
      content:
        '<p>Choosing the wrong register is the single biggest mistake. Use this matrix:</p>',
      table: {
        headers: ['Register', 'Recipient', 'Greeting', 'Sign-off', 'Tone'],
        rows: [
          ['Formal', 'Stranger / institution / company you do not know personally', 'Dear Sir or Madam,', 'Yours faithfully,', 'Polite, distant, no contractions, no idioms'],
          ['Semi-formal', 'Person you know by name but do not know personally (manager, landlord, colleague you respect)', 'Dear Mr Smith,', 'Yours sincerely,', 'Polite, professional, minimal contractions'],
          ['Informal', 'Friend or family member', 'Dear Anna, / Hi Anna,', 'Best wishes, / Take care,', 'Warm, contractions OK, idiomatic phrases welcome'],
        ],
      },
      warning:
        'Never mix registers (e.g., "Dear Sir or Madam" + "Take care, mate"). The examiner will lower your Lexical Resource and Coherence scores.',
    },
    {
      title: 'A Reliable Letter Structure',
      content:
        '<p>Use a 4-paragraph framework regardless of register:</p>',
      points: [
        'Paragraph 1: state your reason for writing in one clear sentence.',
        'Paragraph 2: address bullet 1 with one supporting detail.',
        'Paragraph 3: address bullet 2 and bullet 3 — sometimes split into two short paragraphs.',
        'Paragraph 4: a polite closing sentence + appropriate sign-off.',
      ],
    },
    {
      title: 'Opening Sentences by Register',
      content:
        '<p>Memorise one opening per register so you do not waste time deciding:</p>',
      table: {
        headers: ['Register', 'Opening sentence template'],
        rows: [
          ['Formal', 'I am writing to enquire about / complain about / request information regarding ...'],
          ['Semi-formal', 'I am writing to inform you that ... / I would like to discuss ...'],
          ['Informal', 'How are you? / It has been a while! I just wanted to tell you about ...'],
        ],
      },
    },
    {
      title: 'Useful Phrases by Function',
      content:
        '<p>Functional language signals to the examiner that you handle real letter conventions, not generic essay phrases.</p>',
      table: {
        headers: ['Function', 'Formal', 'Semi-formal', 'Informal'],
        rows: [
          ['Apologising', 'I would like to apologise for ...', 'Please accept my apologies for ...', 'I\'m really sorry about ...'],
          ['Requesting', 'I would be grateful if you could ...', 'Could you please ...', 'Could you ...?'],
          ['Suggesting', 'I would like to propose that ...', 'I think it would be a good idea to ...', 'Why don\'t we ...?'],
          ['Thanking', 'I would like to express my gratitude for ...', 'Thank you very much for ...', 'Thanks so much for ...'],
          ['Closing', 'I look forward to hearing from you.', 'I look forward to your reply.', 'Hope to hear from you soon!'],
        ],
      },
    },
    {
      title: 'Common Mistakes',
      content:
        '<p>These are the errors that drag scores from Band 6 to Band 5:</p>',
      points: [
        'Forgetting the greeting or sign-off.',
        'Mixing "Yours faithfully" with a named recipient (rule: faithfully = unknown, sincerely = named).',
        'Treating a complaint letter like an opinion essay (don\'t list general arguments — describe your specific situation).',
        'Skipping a bullet point (the examiner will mark Task Achievement as incomplete).',
        'Using contractions in formal letters ("I\'m" instead of "I am").',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify the Register',
      type: 'select',
      instructions: 'For each task, choose the correct register.',
      questions: [
        {
          text:
            'Task: "Write a letter to your bank manager explaining a problem with a recent transfer."\nWhich register?',
          options: ['Formal', 'Semi-formal', 'Informal'],
          answer: 'Semi-formal',
          explanation:
            'You know the manager by role/name but not personally. Use "Dear Mr/Ms ..." + "Yours sincerely".',
        },
        {
          text:
            'Task: "Write a letter to a friend who has just moved abroad."\nWhich register?',
          options: ['Formal', 'Semi-formal', 'Informal'],
          answer: 'Informal',
          explanation:
            'Writing to a friend = informal. Use "Dear Anna," / "Hi Anna," and warm closing.',
        },
        {
          text:
            'Task: "Write a letter to the manager of a hotel where you stayed to complain about a problem."\nWhich register?',
          options: ['Formal', 'Semi-formal', 'Informal'],
          answer: 'Formal',
          explanation:
            'You probably do not know the manager personally. Default to formal: "Dear Sir or Madam" + "Yours faithfully".',
        },
        {
          text:
            'Task: "Write a letter to a colleague you work with regularly to ask them to cover your shift."\nWhich register?',
          options: ['Formal', 'Semi-formal', 'Informal'],
          answer: 'Semi-formal',
          explanation:
            'A regular colleague is known but professional. Semi-formal works: "Dear Marie," + "Best regards,".',
        },
      ],
    },
    {
      title: 'Spot the Register Mismatch',
      type: 'select',
      instructions: 'Find the line that breaks the register.',
      questions: [
        {
          text:
            'Letter: "Dear Sir or Madam, I am writing to complain about the service I received last week. The room was honestly a nightmare. I would appreciate a prompt response."\nWhich phrase breaks the formal register?',
          options: [
            'I am writing to complain',
            'The room was honestly a nightmare',
            'I would appreciate a prompt response',
          ],
          answer: 'The room was honestly a nightmare',
          explanation:
            '"Honestly a nightmare" is informal/idiomatic and clashes with the formal greeting. Use "the room was unacceptable" or "below the standard advertised".',
        },
        {
          text:
            'Letter: "Hi Mark! Hope you\'re doing well. I am writing to inform you that I will be unable to attend the football match on Saturday. Yours faithfully, Tom"\nWhich element breaks the register?',
          options: [
            'Hi Mark!',
            'I will be unable to attend',
            'Yours faithfully, Tom',
          ],
          answer: 'Yours faithfully, Tom',
          explanation:
            '"Yours faithfully" is reserved for "Dear Sir or Madam" formal letters. With "Hi Mark!", use "See you, Tom" or "Take care, Tom".',
        },
      ],
    },
    {
      title: 'Match the Sentence to the Bullet',
      type: 'fill-in',
      instructions:
        'Read the prompt and the bullets. Type the bullet number (1, 2, or 3) that the sentence answers.',
      questions: [
        {
          text:
            'Prompt: "Write a letter to your landlord about a problem in your flat."\nBullets:\n1. Explain the problem.\n2. Say how long it has been happening.\n3. Suggest what you would like the landlord to do.\n\nSentence: "I would be grateful if you could send a plumber as soon as possible to repair the leak."\nWhich bullet?',
          answer: '3',
          explanation:
            '"I would be grateful if you could ..." is a polite request — it answers bullet 3 (what action you want).',
        },
        {
          text:
            'Prompt: "Write a letter to your landlord about a problem in your flat."\nBullets:\n1. Explain the problem.\n2. Say how long it has been happening.\n3. Suggest what you would like the landlord to do.\n\nSentence: "The kitchen tap has been leaking continuously since last Tuesday morning."\nWhich bullet?',
          answer: '2',
          explanation:
            '"Since last Tuesday morning" gives the duration, answering bullet 2.',
        },
      ],
    },
    {
      title: 'Write Your Opening Paragraph',
      type: 'writing',
      instructions:
        'Write a 1-2 sentence opening paragraph for the prompt below. State the register you chose.',
      questions: [
        {
          text:
            'Prompt: "Write a letter to a hotel manager to complain about a recent stay. In your letter, explain why you stayed at the hotel, describe the problem, and suggest what you would like them to do."\nWrite your opening paragraph + state the register.',
          example:
            'Register: Formal.\nDear Sir or Madam,\nI am writing to express my dissatisfaction regarding my recent stay at your hotel from 12 to 14 March. Although I had chosen your establishment based on its excellent reputation, several issues during my stay led to a most disappointing experience.',
          tip:
            'Open with "I am writing to ...", state the date/context briefly, and announce that the rest of the letter will explain the issue.',
        },
        {
          text:
            'Prompt: "Write a letter to a friend who has just moved to your city. Invite them out, suggest a place, and ask about their new flat."\nWrite your opening paragraph + state the register.',
          example:
            'Register: Informal.\nHi Sara! How are you settling in? It has been such a long time since I saw you last, and I can\'t believe we are now in the same city again — let\'s catch up properly very soon!',
          tip:
            'Use "How are you?" or "It has been a while!" to open; a friendly tone is expected here, including contractions.',
        },
      ],
    },
  ],
  tips: [
    'Decide the register in the first 30 seconds — it controls every sentence.',
    'Always cover all three bullets. Use 1-2 paragraphs per bullet.',
    'Use "Yours faithfully" only with "Dear Sir or Madam"; "Yours sincerely" with named recipients.',
    'Aim for ~170-190 words. Below 150 words = automatic penalty on Task Achievement.',
    'Use functional language ("I would be grateful if...", "Please accept my apologies for...") to lift Lexical Resource.',
  ],
  dailyPractice: [
    'Write one letter a day from a single bullet-point prompt — 12 minutes max.',
    'Memorise one opening + one closing sentence per register.',
    'Take a friend\'s informal email and rewrite it in formal register, then in semi-formal.',
    'Time yourself: 20 minutes maximum for Task 1, including 2 minutes of planning.',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext:
    'IELTS General Training Task 1 letters: choosing the correct register (formal, semi-formal, informal), structuring the letter, covering all bullet points, and using register-appropriate functional language.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...task1GeneralLettersData,
      title: task1GeneralLettersData.title + ' (Foundations)',
      overview: { ...task1GeneralLettersData.overview, duration: '20-25 minutes' },
      sections: task1GeneralLettersData.sections.slice(0, 4),
      exercises: [
        {
          ...task1GeneralLettersData.exercises[0],
          title: 'Identify the Register (Basic)',
          questions: task1GeneralLettersData.exercises[0].questions.slice(0, 3),
        },
        {
          ...task1GeneralLettersData.exercises[2],
          title: 'Match the Sentence to the Bullet (Basic)',
          questions: task1GeneralLettersData.exercises[2].questions.slice(0, 1),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...task1GeneralLettersData,
      title: task1GeneralLettersData.title + ' (Advanced)',
      overview: { ...task1GeneralLettersData.overview, duration: '35-40 minutes' },
      sections: [
        ...task1GeneralLettersData.sections,
        {
          title: 'Adding Range Without Breaking Register',
          content:
            '<p>At Band 7+, examiners look for register-specific lexical range. Practise these upgrades:</p>',
          points: [
            'Formal: replace "I want" with "I would like", "tell" with "inform", "ask" with "request".',
            'Semi-formal: prefer "I appreciate" over "Thanks a lot", "I trust" over "I hope".',
            'Informal: idioms are welcome ("by the way", "no worries", "catch up soon"), but spelling and grammar must remain accurate.',
          ],
        },
      ],
      exercises: [
        ...task1GeneralLettersData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
      ],
      tips: [
        ...(task1GeneralLettersData.tips || []),
        'At Band 7+, vary your sentence structures inside each paragraph (one short + one complex).',
        'Use one register-specific idiom or fixed expression per paragraph to demonstrate range.',
      ],
    };
  }
  return task1GeneralLettersData;
}
